/**
 * Results (AIC-179, AIC-182): how a result's fields read on screen. GET /results/:id returns the fields in spec order,
 * each with the spec topic it was asked under ({id, title}), the field catalogue's label (null for an id the catalogue
 * doesn't know) and its reporting basis; grouping, labels and the basis line come from there only (AIC-391: the
 * fallback that re-derived them from the field_id for an older tracker-api is gone, since tracker-api's
 * results/fields.ts always sends them, for GET /results/:id and the Review queue's evidence_fields alike). A field
 * without a label reads as describeField reads its id (an id the catalogue no longer parses, or doesn't know), and one
 * without a topic is shown under "Other fields", never dropped.
 * describeField remains for ids that come with no server label: a Review item's own field_id and the figures parsed
 * from its evidence text (ReviewItemDetail, ReviewEvidence).
 * The API's objects are loose: basis, source (provenance), a transcript offset, red flags and the verifier's summary
 * are read here when tracker-api sends them, and simply not shown when it doesn't.
 */
import { holdsApprove, waitsOnVendor } from "@swtracker/core/concern-gate";
import { concernTypeLabel } from "@swtracker/core/display-labels";
import { periodCodeLabel, periodCodesInText } from "@swtracker/core/period-label";
import { cleanHeadlineText } from "@swtracker/core/review-copy";
import { z } from "zod";
import type { ConcernCalculation, ResultConcern as ApiConcern, ResultDetail, ResultField } from "../api/schemas.ts";
import type { Tone } from "../components/status.tsx";

/** A field the payload gives no topic: grouped last, under this heading. */
const OTHER_TOPIC = { id: "other", title: "Other fields" } as const;

const BASE_LABELS: Readonly<Record<string, string>> = {
  fye_month: "Fiscal year end month",
  ownership: "Ownership",
  hq_country: "Headquarters country",
  employees: "Employees",
  sw_rev_total: "Software revenue",
  iaas_rev_total: "Public Cloud IaaS revenue",
  svc_rev_total: "Services revenue",
  hw_rev_total: "Hardware revenue",
  other_rev_total: "Other revenue",
  share_region: "Revenue by region",
  share_deploy: "Revenue by deployment type",
  share_market: "Revenue by functional market",
  channel_direct_pct: "Direct sales (% direct)",
  acquisitions_confirm: "Acquisitions confirmed",
  open_comments: "Anything else IDC should know",
};

/**
 * A label for a field id that comes with none from the server (a Review item's field_id, an id in evidence text):
 * "sw_rev_total:2025" → Software revenue · 2025; an unknown id is itself.
 */
export function describeField(fieldId: string): { label: string; detail: string } {
  const [base = fieldId, ...rest] = fieldId.split(":");
  const known = BASE_LABELS[base];
  return known ? { label: known, detail: rest.map(periodCodeLabel).join(" · ") } : { label: fieldId, detail: "" };
}

/** A catalogue field id inside prose ("sw_rev_total:2025", "share_region:2025:EMEA"), whole identifiers only. */
const KEY_TAIL = "(?::[A-Za-z0-9_&/().-]+(?: [A-Z][A-Za-z0-9&/().-]*)*)?";
const PERIOD = "\\d{4}(?:H[12])?";
/** Bases with an underscore are ids on their own; a single word ("employees", "ownership") is an id only with its period, else it is English. */
const BASES = Object.keys(BASE_LABELS);
/**
 * Edges of an id: no word character or ":" before it, and after it no word character, ":" then text (a colon before
 * whitespace or the end ends the id: "sw_rev_total:2025: confirmed", AIC-585) or "." then a letter (".pdf"). "/", "-"
 * and ".5" after an id are prose ("sw_rev_total:2025/2024", "-based"); a sentence-ending period ends it.
 * File names, paths, URLs and emails are protected as whole tokens by LINK, not by these edges.
 */
const ID_BEFORE = "(?<![\\w:])";
const ID_AFTER = "(?![\\w]|:\\S|\\.[a-z])";
const FIELD_ID_IN_TEXT = new RegExp(`${ID_BEFORE}(?:(?:${BASES.filter((b) => b.includes("_")).join("|")})(?::${PERIOD}${KEY_TAIL})?|(?:${BASES.filter((b) => !b.includes("_")).join("|")}):${PERIOD}${KEY_TAIL})${ID_AFTER}`, "g");
/** A URL, email, absolute / ./ ~/ path or a token with a file extension: kept exactly as written (AIC-571, AIC-585). */
const LINK = /(\S+:\/\/\S+|\S+@\S+|(?<!\S)[~.]?\/\S+|\S*\.[a-z][a-z0-9]{1,4}\b\S*)/;
/**
 * Splits text into prose and link parts (odd indexes are links). Trailing sentence punctuation and an unbalanced
 * closing bracket are not part of a link: "(see https://x.example/a)." keeps its ")" and "." as prose.
 */
function splitLinks(text: string): string[] {
  const parts = text.split(LINK);
  for (let i = 1; i < parts.length; i += 2) {
    let link = parts[i]!;
    let tail = "";
    for (;;) {
      const last = link.slice(-1);
      const open = last === ")" ? "(" : last === "]" ? "[" : null;
      const unbalanced = open !== null && link.split(last).length > link.split(open).length;
      if (!/[.,;:!?'"]/.test(last) && !unbalanced) break;
      tail = last + tail;
      link = link.slice(0, -1);
    }
    parts[i] = link;
    parts[i + 1] = tail + (parts[i + 1] ?? "");
  }
  return parts;
}
/** Runs `fn` on the text between links, which are kept whole. */
export const outsideLinks = (text: string, fn: (part: string) => string): string => splitLinks(text).map((part, i) => (i % 2 ? part : fn(part))).join("");
/** Every catalogue field id in the text written as its label and period ("Software revenue · 2025"); other text is left alone. */
export const labelFieldIds = (text: string): string => text.replace(FIELD_ID_IN_TEXT, (id) => { const d = describeField(id); return d.detail ? `${d.label} · ${d.detail}` : d.label; });

const payloadField = z.object({
  label: z.string().min(1).nullable().optional().catch(undefined),
  topic: z.object({ id: z.string().min(1), title: z.string().min(1) }).nullable().optional().catch(undefined),
}).loose();
const payloadTopic = (f: Pick<ResultField, "field_id">) => {
  const x = payloadField.safeParse(f);
  return x.success ? x.data.topic ?? null : null;
};

/**
 * A result field's label and topic, as the payload (AIC-182) gives them: the label with the id's period and key as
 * detail; "Other fields" with no topic. When the label is null, describeField's reading of the id: the catalogue
 * returns null for a stored id whose period no longer parses after the PERIODS / SPLIT_YEAR roll-forward
 * ("sw_rev_total:2024"), which still reads "Software revenue · 2024"; an id with an unknown base is shown as it is.
 */
export function fieldView(f: Pick<ResultField, "field_id">): { label: string; detail: string; topic: { id: string; title: string } } {
  const x = payloadField.safeParse(f);
  const label = x.success ? x.data.label ?? null : null;
  const local = label ? null : describeField(f.field_id);
  return {
    label: label ?? local!.label,
    detail: label ? f.field_id.split(":").slice(1).map(periodCodeLabel).join(" · ") : local!.detail,
    topic: payloadTopic(f) ?? OTHER_TOPIC,
  };
}

/** Changed by the vendor: the rows the analyst checks first (a gold edge, and "changed" for screen readers). */
const CHANGED = new Set(["corrected", "refined", "added"]);
export const isChanged = (f: ResultField) => f.status !== null && CHANGED.has(f.status);
/** The verifier doesn't back the answer: disagrees, or unclear. */
export const isFlagged = (f: ResultField) => f.verdict === "disagrees" || f.verdict === "unclear";

const ANSWER: Readonly<Record<string, { label: string; tone: Tone }>> = {
  confirmed: { label: "Confirmed", tone: "done" },
  corrected: { label: "Corrected", tone: "you" },
  refined: { label: "Refined", tone: "you" },
  added: { label: "Added", tone: "you" },
  declined: { label: "Declined", tone: "off" },
};
const VERDICT: Readonly<Record<string, { label: string; tone: Tone }>> = {
  supported: { label: "Supported", tone: "done" },
  disagrees: { label: "Disagrees", tone: "risk" },
  unclear: { label: "Unclear", tone: "you" },
  not_discussed: { label: "Not discussed", tone: "off" },
};
/** "basis_mismatch" → "Basis mismatch". */
export const humanize = (s: string) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export const answerOf = (status: string | null) => (status ? ANSWER[status] ?? { label: humanize(status), tone: "wait" as Tone } : { label: "Not answered", tone: "off" as Tone });
export const verdictOf = (verdict: string | null) => (verdict ? VERDICT[verdict] ?? { label: humanize(verdict), tone: "wait" as Tone } : null);

const SOURCE: Readonly<Record<string, string>> = { voice: "Voice interview", typed: "Typed in the interview", form: "Text form" };

const fieldExtras = z.object({
  basis: z.enum(["calendar", "fiscal"]).nullable().optional().catch(undefined),
  source: z.string().nullable().optional().catch(undefined),
  transcript_offset_s: z.number().nonnegative().nullable().optional().catch(undefined),
}).loose();

/** What the payload may carry beyond the typed fields (basis, where the answer came from, where in the call). */
export function extrasOf(f: ResultField): { basis: "calendar" | "fiscal" | null; source: string | null; at: string | null; offset: number | null } {
  const x = fieldExtras.safeParse(f);
  const d = x.success ? x.data : {};
  const s = d.transcript_offset_s;
  const at = typeof s === "number" ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : null;
  return { basis: d.basis ?? null, source: d.source ? SOURCE[d.source] ?? null : null, at, offset: typeof s === "number" ? Math.floor(s) : null };
}

const reportingBasisExtra = z.object({
  reporting_basis: z.object({
    basis: z.enum(["calendar", "fiscal"]).nullable(),
    from: z.enum(["vendor", "idc_estimate"]).nullable(),
  }).nullable().optional().catch(undefined),
}).loose();

interface ReportingBasis { readonly basis: "calendar" | "fiscal" | null; readonly from: "vendor" | "idc_estimate" | null }

/**
 * A field's reporting basis (AIC-241), or null for a field that carries none: tracker-api's `reporting_basis`
 * (tracker-data reportingBasis) decides. Null for a field that carries no basis (only revenue lines do) or has no
 * answer; IDC's calendar basis for a revenue confirmation that recorded none, marked as inherited; unknown for a
 * changed value without one. A payload without it is read as carrying none (AIC-391: no local re-derivation).
 */
export function reportingBasisOf(f: ResultField): ReportingBasis | null {
  const x = reportingBasisExtra.safeParse(f);
  return (x.success ? x.data.reporting_basis : undefined) ?? null;
}

/** The reporting-basis line under a field's answer (AIC-241), or null for none; reportingBasisOf decides. */
export function basisLabel(f: ResultField): string | null {
  const rb = reportingBasisOf(f);
  if (!rb) return null;
  if (!rb.basis) return "Reporting basis unknown";
  return rb.from === "idc_estimate" ? "Calendar year (IDC basis)" : `${rb.basis === "calendar" ? "Calendar" : "Fiscal"} basis`;
}

/** The vendor asked to keep this answer confidential (flag_sensitive; tracker-api's per-field `sensitive`, AIC-210), or the session has an open hold (AIC-551). */
export const isSensitive = (f: ResultField): boolean => (f as { sensitive?: unknown }).sensitive === true;

/** The answer itself is confidential, so its evidence is not exported to Snowflake (tracker-api's `evidence_withheld`, AIC-551). */
export const evidenceWithheld = (f: ResultField): boolean => (f as { evidence_withheld?: unknown }).evidence_withheld === true;

/** The call record at a field's moment: the result's call_record_url with #t=<seconds>, when there is one (AIC-182). */
export function callRecordAt(r: Pick<ResultDetail, "call_record_url">, offset: number | null): string | null {
  if (!r.call_record_url) return null;
  return offset === null ? r.call_record_url : `${r.call_record_url}#t=${offset}`;
}

const verifierExtras = z.object({
  red_flags: z.array(z.object({ category: z.string(), evidence: z.string().nullable().optional() }).loose()).optional().catch(undefined),
  // Stored as the verifier's verificationSummary (packages/core tools/catalog.ts): analyst_summary, tier_rationale and
  // per-topic outcomes. A plain string is read as the summary.
  summary: z.union([
    z.string(),
    z.object({
      analyst_summary: z.string().optional().catch(undefined),
      tier_rationale: z.string().optional().catch(undefined),
      topics: z.array(z.object({ title: z.string(), status: z.string(), vendor_outcome: z.string().nullable().optional(), notes: z.string().nullable().optional() }).loose()).optional().catch(undefined),
    }).loose(),
  ]).nullable().optional().catch(undefined),
}).loose();

interface VerifierView {
  readonly redFlags: readonly { category: string; evidence: string | null }[] | null;
  readonly summary: string | null;
  readonly rationale: string | null;
  readonly topics: readonly { title: string; status: string; outcome: string | null }[];
}

/** The verifier's red flags and summary, when the result payload carries them (null / empty otherwise). */
export function verifierOf(r: ResultDetail): VerifierView {
  const x = verifierExtras.safeParse(r);
  const d = x.success ? x.data : {};
  const s = d.summary;
  const o = s && typeof s === "object" ? s : null;
  return {
    redFlags: d.red_flags ? d.red_flags.map((f) => ({ category: concernTypeLabel(f.category), evidence: f.evidence ?? null })) : null,
    summary: typeof s === "string" ? s : o?.analyst_summary || null,
    rationale: o?.tier_rationale || null,
    topics: o?.topics ? o.topics.map((t) => ({ title: t.title, status: humanize(t.status.toLowerCase()), outcome: t.vendor_outcome ?? t.notes ?? null })) : [],
  };
}

/**
 * Fields grouped by the topic the payload names, topics in the order the spec asks them and fields in the spec's order
 * within a topic. Fields without a topic come last, under "Other fields" (or join a named topic "other").
 */
export function groupByTopic(fields: readonly ResultField[]): { id: string; title: string; fields: ResultField[] }[] {
  const named = new Map<string, { id: string; title: string; fields: ResultField[] }>();
  const untopiced: ResultField[] = [];
  for (const f of fields) {
    const t = payloadTopic(f);
    if (!t) { untopiced.push(f); continue; }
    const g = named.get(t.id) ?? { id: t.id, title: t.title, fields: [] };
    g.fields.push(f);
    named.set(t.id, g);
  }
  if (untopiced.length) {
    const other = named.get(OTHER_TOPIC.id);
    if (other) other.fields.push(...untopiced);
    else named.set(OTHER_TOPIC.id, { ...OTHER_TOPIC, fields: untopiced });
  }
  return [...named.values()];
}

/** Summarize only explicit per-field provenance; a quote or timestamp proves no source. */
export function resultSources(fields: readonly ResultField[]): string {
  const sources = [...new Set(fields.map((f) => extrasOf(f).source ?? "Unknown source"))];
  return sources.length ? sources.join(" · ") : "Unknown source";
}

/** What a verifier calculation tool is called on screen; analysts never see the tool id (AIC-478, AIC-541). */
export const CALCULATION_LABEL: Record<ConcernCalculation["tool"], string> = {
  calendarize_figure: "fiscal-to-calendar year conversion",
  check_period_share: "half-year share check",
};
export const calculationLabel = (tool: string): string => (CALCULATION_LABEL as Record<string, string>)[tool] ?? "calculation";

/** A field's name on screen: its label and detail ("Software revenue · 2025"). */
export function fieldName(f: Pick<ResultField, "field_id">): string {
  const view = fieldView(f);
  return [view.label, view.detail].filter(Boolean).join(" · ");
}

/**
 * Matches the result's field ids as whole identifiers, longest first (never a shorter id inside another one). Only
 * code-shaped ids (with an "_" or ":") are matched: a plain-word id ("ownership", "employees") is English in
 * prose, and swapping it for its label garbles the sentence (AIC-567). Matching is case-sensitive, as ids are.
 */
function idPattern(fields: readonly Pick<ResultField, "field_id">[]): RegExp | null {
  const ids = [...new Set(fields.map((f) => f.field_id).filter((id) => /[_:]/.test(id)))].sort((a, b) => b.length - a.length);
  if (!ids.length) return null;
  const escaped = ids.map((id) => id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`${ID_BEFORE}(?:${escaped.join("|")})${ID_AFTER}`, "g");
}

/** Display known metric identifiers as labels without interpreting the verifier's claim. `periods: false` (the vendor's own quote) leaves a bare period code as the vendor said it. */
export function readableEvidence(text: string, fields: readonly ResultField[], periods = true): string {
  const names = new Map(fields.map((f) => [f.field_id, fieldName(f)]));
  const re = idPattern(fields);
  const own = (part: string): string => (re ? part.replace(re, (id) => names.get(id) ?? id) : part);
  // An id the result has no field for (another year, another region) reads as the catalogue's label, not raw, and is never left for plain()'s identifier pass to delete.
  // URLs and emails are kept whole (AIC-571).
  const named = outsideLinks(text, (part) => labelFieldIds(own(part)));
  // A bare period code in the verifier's prose reads as the label too ("2026H1" → "H1 2026", AIC-571); nothing else is touched.
  return (periods ? periodCodesInText(named) : named).replace(/\busd_m\b/g, "USD millions");
}

/**
 * Analyst-facing text: field ids by label first, then the shared headline cleaner (tool names, [mm:ss] stamps with their
 * lead-ins, leftover ids). Every verifier sentence shown on the Results screens goes through this (AIC-478, AIC-541, AIC-557).
 */
export function plain(text: string, fields: readonly ResultField[]): string {
  // Links are masked so the cleaner's leftover-identifier pass cannot delete part of a URL, email, path or file name (AIC-585).
  const kept: string[] = [];
  const nonce = Math.random().toString(36).slice(2, 10);
  const masked = splitLinks(readableEvidence(text, fields)).map((part, i) => (i % 2 ? `\uE000${nonce}${kept.push(part) - 1}\uE001` : part)).join("");
  return cleanHeadlineText(masked).replace(new RegExp(`\uE000${nonce}(\\d+)\uE001`, "g"), (_, n: string) => kept[Number(n)] ?? "");
}

/**
 * What the Results screens show of the verifier's own words: only the field ids, the unit code and the calculation tool
 * names are re-worded; nothing is removed (times, asides and figures stay). The original is in the card's disclosure.
 */
export function relabel(text: string, fields: readonly ResultField[]): string {
  return readableEvidence(text, fields).replace(/\b(?:calendarize_figure|check_period_share)\b/g, (tool) => calculationLabel(tool));
}

/** A revenue line (sw_rev_total:2025 …): the only fields that carry a reporting basis. */
export const isRevenueField = (f: Pick<ResultField, "field_id">): boolean => /^[a-z]+_rev_total(?::|$)/.test(f.field_id);

/**
 * One result concern as the Results page shows it (AIC-257). With AIC-258/259's `concerns[]` (`api` set) it is the
 * stored concern: its id, its one `field_id`, summary, blocking, recommendation and decision. From an older payload it
 * is a verifier red flag (AIC-255): `fieldIds` are the fields its text names by id, `blocking` is unknown (null) and
 * there is no decision to record.
 */
export interface ResultConcern {
  /** The DOM id of its card (rs-concern-…). */
  readonly id: string;
  readonly category: string;
  readonly evidence: string | null;
  readonly fieldIds: readonly string[];
  readonly blocking: boolean | null;
  readonly api: ApiConcern | null;
}

/** The result's concerns: the stored ones (AIC-258), else its red flags; null when the payload carries neither. */
export function concernsOf(r: ResultDetail, stored: readonly ApiConcern[] | undefined = r.concerns): ResultConcern[] | null {
  const flags = verifierOf(r).redFlags;
  // A result verified before 0032 has no stored concern rows (no backfill), so an empty stored list with red flags
  // means "older result", not "no concerns": show its red flags.
  if (stored && (stored.length > 0 || !flags?.length)) {
    // A concern stored without its own field_id (a red flag from before the column was filled) may name its field in its
    // text. It is attached only when that is unambiguous: exactly one distinct id of this result's fields is named, or
    // (with a period-share check) exactly one half-year id. Otherwise nothing is attached rather than the wrong field.
    const re = idPattern(r.fields);
    const named = (c: ApiConcern): string[] => {
      if (c.field_id) return [c.field_id];
      const text = [c.summary, c.recommendation?.rationale].filter((t): t is string => typeof t === "string").join(" ");
      const ids = [...new Set(re ? text.match(re) ?? [] : [])];
      if (ids.length === 1) return ids;
      const halves = ids.filter((id) => /:\d{4}H[12]$/.test(id));
      const hasShare = (c.recommendation?.calculations ?? []).some((x) => x.tool === "check_period_share");
      return hasShare && halves.length === 1 ? halves : [];
    };
    return stored.map((c) => ({
      id: `rs-concern-${c.id}`, category: concernTypeLabel(c.type), evidence: c.summary, fieldIds: named(c), blocking: c.blocking, api: c,
    }));
  }
  if (!flags) return null;
  const re = idPattern(r.fields);
  return flags.map((flag, i) => {
    const named = new Set(flag.evidence && re ? flag.evidence.match(re) ?? [] : []);
    return { id: `rs-concern-${i}`, category: flag.category, evidence: flag.evidence, fieldIds: r.fields.map((f) => f.field_id).filter((id) => named.has(id)), blocking: null, api: null };
  });
}

/** Still needs the analyst's decision: a stored concern without a disposition. A red flag can't be decided, so it always counts. */
export const isUndecided = (c: ResultConcern): boolean => !c.api || c.api.disposition === null;

/**
 * The approve gate's settle rule (AIC-274, Bill's option B), shared with tracker-api's undecidedBlocking: a data concern
 * holds Approve while the gate is on until it is Resolved or Accept with note; Needs follow-up is waiting on the vendor.
 */
export { holdsApprove, waitsOnVendor };

/**
 * Still to settle for the banner: undecided, or (gate on) a data concern waiting on the vendor. A red flag can't be
 * decided, so it always counts.
 */
export const isUnsettled = (c: ResultConcern, gateOn: boolean): boolean => isUndecided(c) || (!!c.api && waitsOnVendor(c.api, gateOn));
export const WAITING_HOLDS_APPROVE = "Waiting on the vendor: holds Approve";

/** The disposition's label on screen (AIC-259). */
export const DISPOSITION_LABEL: Readonly<Record<string, string>> = {
  resolved: "Resolved", accepted: "Accept with note", follow_up: "Needs follow-up", follow_up_after_approval: "Follow up after approval",
};

const PRIOR_SOURCE: Readonly<Record<string, string>> = { aurora: "an approved result", sw_tracker: "the Data Layer", survey_snapshot: "a returned survey" };

/**
 * The prior-cycle line on a field (AIC-262), or null when the payload says nothing (an older API): the latest earlier
 * answer; "No earlier answer on record" when every source was read; "couldn't be checked" when one wasn't.
 */
export function priorLine(f: ResultField, unavailable: readonly string[] | undefined, format: (v: unknown, unit: string | null) => string): string | null {
  if (f.prior === undefined) return null;
  const missing = (unavailable ?? []).length > 0;
  if (f.prior === null) return missing ? "Earlier cycles couldn't be checked" : "No earlier answer on record";
  const p = f.prior;
  return [`Last cycle (${periodCodeLabel(p.cycle)}): ${format(p.value, f.unit)}`, p.basis, `from ${PRIOR_SOURCE[p.source] ?? p.source}`].filter(Boolean).join(" · ")
    + (missing ? ". Some earlier sources couldn't be checked" : "");
}

/** A field with a concern: the verifier doesn't back it (isFlagged), or a result concern names it. */
export function hasConcern(f: ResultField, concerns: readonly ResultConcern[] | null): boolean {
  return isFlagged(f) || (concerns ?? []).some((c) => c.fieldIds.includes(f.field_id));
}
